import { inject, Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AppSnackbar, AppSnackbarData, SnackSeverity } from '../shared/ui/app-snackbar/app-snackbar';

export interface SnackbarOptions {
  /** Optionaler Untertitel / Kommentar unterhalb des Titels */
  message?: string;
  /** Liste von Detail-Einträgen (z. B. Felder einer neuen Kontrolle) */
  entries?: string[];
  /** Anzeigedauer in ms – Standard: 4000 */
  duration?: number;
}

/**
 * SnackbarService
 *
 * Zentraler Service für App-weite Feedback-Benachrichtigungen.
 * Stellt typisierte Methoden je Schweregrad bereit:
 *   success  – grüne Bestätigung (Aktion erfolgreich)
 *   warning  – gelber Hinweis (Aktion möglich, aber mit Vorbehalt)
 *   error    – roter Fehler (Aktion fehlgeschlagen / blockiert)
 *   info     – blauer Info-Hinweis (neutral)
 */
@Injectable({
  providedIn: 'root',
})
export class SnackbarService {
  private readonly snackBar = inject(MatSnackBar);

  success(title: string, options?: SnackbarOptions): void {
    this.open('success', title, options);
  }

  error(title: string, options?: SnackbarOptions): void {
    this.open('error', title, options);
  }

  warning(title: string, options?: SnackbarOptions): void {
    this.open('warning', title, options);
  }

  info(title: string, options?: SnackbarOptions): void {
    this.open('info', title, options);
  }

  private open(severity: SnackSeverity, title: string, options?: SnackbarOptions): void {
    const data: AppSnackbarData = {
      severity,
      title,
      message: options?.message,
      entries: options?.entries
        ?.map(e => (e == null ? '' : String(e)))
        .filter(e => e.trim().length > 0),
    };

    this.snackBar.openFromComponent(AppSnackbar, {
      panelClass: 'app-snackbar',
      data,
      duration:           options?.duration ?? 4000,
      horizontalPosition: 'right',
      verticalPosition:   'bottom',
    });
  }
}

